import type { IZoomPanPinchRef } from '@monofe/ui-common';
import { Image as WSImage, ZoomPanPinch } from '@monofe/ui-common';
import type { IAssetSource } from '@monofe/util';
import type { CSSProperties, RefObject } from 'react';
import { memo, useEffect, useState } from 'react';
import { StyleElement, classNameForElement, styleForElement } from './ItemView.style';
import MarkerView from './MarkerView';
import type { IFloorplanItemViewModel, IFloorplanMarkerViewModel } from './interface';

interface ISize {
    width: number;
    height: number;
}

const SIZE_ZERO: ISize = Object.freeze({ width: 0, height: 0 });

function useImageSize(imageUrls: IAssetSource[]): ISize {
    const [imageSize, setImageSize] = useState<ISize>(SIZE_ZERO);

    useEffect(() => {
        if (imageUrls.length <= 0) {
            setImageSize(SIZE_ZERO);
            return;
        }

        const promise: Promise<ISize> = new Promise((resolve, reject) => {
            const picture = document.createElement('picture');
            const image = document.createElement('img');

            for (const url of imageUrls) {
                const source = document.createElement('source');

                source.srcset = url.src;
                source.type = url.type ?? '';

                picture.append(source);
            }

            image.addEventListener('load', () => {
                resolve({
                    width: image.width,
                    height: image.height,
                });
            });

            picture.append(image);

            image.addEventListener('error', () => {
                reject(`Failed to load image for floorplan`);
            });
        });

        promise
            .then(sizeNew => {
                setImageSize(sizeNew);
            })
            .catch((error: Error) => {
                throw new Error(
                    `Failed to set ImageSize, urls:${JSON.stringify(
                        imageUrls,
                    )}, error:${JSON.stringify(error)}`,
                );
            });
    }, [imageUrls]);

    return imageSize;
}

function _markerStylePositionX(item: IFloorplanMarkerViewModel, size: ISize): string {
    const pos = Math.round((item.positionX / size.width) * 100);
    return `${pos.toString()}%`;
}

function _markerStylePositionY(item: IFloorplanMarkerViewModel, size: ISize): string {
    const pos = Math.round((item.positionY / size.height) * 100);
    return `${pos.toString()}%`;
}

/* Changes to useState values will trigger re-render */
function useForceUpdate(): () => void {
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    const [value, setValue] = useState(0);
    return () => {
        setValue(value => value + 1);
    };
}

function ItemView(
    properties: IFloorplanItemViewModel & {
        imageRef: RefObject<IZoomPanPinchRef>;
        className: string;
        style?: CSSProperties;
    },
): JSX.Element {
    const style = (element: StyleElement, overrides?: CSSProperties) =>
        styleForElement(element, properties, overrides);
    const className = (element: StyleElement) => classNameForElement(element, properties);

    const imageSize = useImageSize(properties.imageUrls);
    const triggerRender = useForceUpdate();

    const hasLoadedImage = imageSize.width > 0 && imageSize.height > 0;
    const imageScale = properties.imageRef.current?.state.scale ?? 1;
    const markerStyle: CSSProperties = {
        transform: `scale(${1 / imageScale})`,
        opacity: 1,
    };

    const ZoomView = memo(ZoomPanPinch());

    return (
        <div
            className={`${className(StyleElement.Container)} ${properties.className ?? ''}`}
            style={style(StyleElement.Container, properties.style)}
        >
            <ZoomView
                startX={properties.startX}
                startY={properties.startY}
                startZoom={properties.startZoom}
                minZoom={properties.minZoom}
                maxZoom={properties.maxZoom}
                onZoom={triggerRender}
                ref={properties.imageRef}
            >
                <div
                    className={className(StyleElement.MarkerContainer)}
                    style={style(StyleElement.MarkerContainer)}
                >
                    <WSImage
                        aria-hidden
                        description={properties.name}
                        urls={properties.imageUrls}
                        className={className(StyleElement.Image)}
                        style={style(StyleElement.Image)}
                    />
                    {hasLoadedImage &&
                        properties.markers.map((item, index) => (
                            <MarkerView
                                key={`${index}-${item.title.text}`}
                                style={markerStyle}
                                isVisible={item.isVisible}
                                title={item.title}
                                color={item.color}
                                onClick={
                                    typeof item.onClick === 'string'
                                        ? item.onClick
                                        : () => {
                                              typeof item.onClick === 'function' &&
                                                  item.onClick(item);
                                          }
                                }
                                positionX={_markerStylePositionX(item, imageSize)}
                                positionY={_markerStylePositionY(item, imageSize)}
                            />
                        ))}
                </div>
            </ZoomView>
        </div>
    );
}

export default memo(ItemView, (a, b) => Object.is(a, b));
